import { useRef, useEffect, useState, useCallback, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useParticipants, useLocalParticipant, useTracks, useRoomContext } from '@livekit/components-react';
import { Track, RoomEvent, Participant } from 'livekit-client';
import { useRoom } from '../room/RoomProvider';
import { MediaControls } from './MediaControls';
import { ValueAnalysisOverlay, ValueOverlayMode } from './ValueAnalysisOverlay';
import { logVideoDiagnostic } from './videoDiagnostics';
import { isActiveModelCameraParticipant } from './liveKitCameraProfiles';
import {
  createActiveSpeakerSwitchState,
  selectActiveSpeakerUpdate,
  selectFeaturedParticipant,
} from './featuredParticipantSelection';
import type { PoseHeartPayload, ViewerOrientation } from '@modelcast/types';

// Grid overlay density levels
type GridDensity = 'off' | 'sparse' | 'dense';
interface PoseHeartBurst {
  id: string;
  groupId: string;
  seed: number;
  index: number;
}
const POSE_HEARTS_PER_BURST = 10;
const POSE_HEART_ANIMATION_MS = 3400;
const POSE_HEART_STAGGER_MS = 42;
const POSE_HEART_CLEANUP_MS = POSE_HEART_ANIMATION_MS + POSE_HEART_STAGGER_MS * POSE_HEARTS_PER_BURST + 300;

// Grid overlay component for proportion reference - renders perfect squares
function GridOverlay({ density, containerRef }: { density: GridDensity; containerRef: React.RefObject<HTMLDivElement> }) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  if (density === 'off' || dimensions.width === 0 || dimensions.height === 0) return null;

  // Calculate square size based on the smaller dimension
  const gridCount = density === 'sparse' ? 6 : 12; // Number of squares along shorter edge
  const squareSize = Math.min(dimensions.width, dimensions.height) / gridCount;

  // Calculate how many squares fit in each direction
  const horizontalCount = Math.ceil(dimensions.width / squareSize);
  const verticalCount = Math.ceil(dimensions.height / squareSize);

  // Center the grid
  const totalGridWidth = horizontalCount * squareSize;
  const totalGridHeight = verticalCount * squareSize;
  const offsetX = (dimensions.width - totalGridWidth) / 2;
  const offsetY = (dimensions.height - totalGridHeight) / 2;

  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      {/* Vertical lines */}
      {Array.from({ length: horizontalCount + 1 }).map((_, i) => (
        <div
          key={`v-${i}`}
          className="absolute top-0 bottom-0 w-px bg-white/30"
          style={{ left: offsetX + i * squareSize }}
        />
      ))}
      {/* Horizontal lines */}
      {Array.from({ length: verticalCount + 1 }).map((_, i) => (
        <div
          key={`h-${i}`}
          className="absolute left-0 right-0 h-px bg-white/30"
          style={{ top: offsetY + i * squareSize }}
        />
      ))}
    </div>
  );
}

// Grid toggle button
function GridToggleButton({
  density,
  onToggle,
  className = '',
}: {
  density: GridDensity;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onToggle}
      className={`z-10 h-8 px-2.5 text-[11px] font-medium bg-[rgba(10,6,5,0.62)] backdrop-blur-md border border-white/12 rounded text-white hover:bg-black/70 transition-colors flex items-center gap-1.5 ${className}`}
      title={density === 'off' ? 'Show grid' : density === 'sparse' ? 'Denser grid' : 'Hide grid'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        {/* Grid icon */}
        <rect x="3" y="3" width="18" height="18" rx="1" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="3" y1="15" x2="21" y2="15" />
        <line x1="9" y1="3" x2="9" y2="21" />
        <line x1="15" y1="3" x2="15" y2="21" />
      </svg>
      {density !== 'off' && (
        <span className="text-[10px] opacity-70">{density === 'sparse' ? '6' : '12'}</span>
      )}
    </button>
  );
}

// B&W toggle button
function BWToggleButton({
  mode,
  onToggle,
  className = '',
}: {
  mode: ValueOverlayMode;
  onToggle: () => void;
  className?: string;
}) {
  const isActive = mode !== 'off';
  const label = mode === 'grayscale' ? 'B&W' : mode.startsWith('bands-') ? mode.replace('bands-', '') : 'B&W';
  const title =
    mode === 'off'
      ? 'Black & white'
    : mode === 'grayscale'
        ? 'Show 8 value bands'
        : mode === 'bands-8'
          ? 'Show 5 value bands'
          : mode === 'bands-5'
            ? 'Show 3 value bands'
            : 'Show color';

  return (
    <button
      onClick={onToggle}
      className={`z-10 h-8 px-2.5 text-[11px] font-medium bg-[rgba(10,6,5,0.62)] backdrop-blur-md border border-white/12 rounded text-white hover:bg-black/70 transition-colors flex items-center gap-1.5 ${isActive ? 'ring-1 ring-white/40' : ''} ${className}`}
      title={title}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        {/* Half circle icon for B&W */}
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v18" />
        <path d="M12 3a9 9 0 0 1 0 18" fill="currentColor" />
      </svg>
      <span className="text-[10px]">{label}</span>
    </button>
  );
}

function PoseHeartButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="z-10 h-8 w-8 rounded border border-white/12 bg-[rgba(10,6,5,0.62)] text-[#ff6f91] backdrop-blur-md transition-colors hover:bg-black/70 hover:text-[#ff8fab] flex items-center justify-center"
      title="Send a heart"
      aria-label="Send a heart"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.8 4.6c-1.7-1.6-4.4-1.5-6 .2L12 7.7 9.2 4.8c-1.6-1.7-4.3-1.8-6-.2-1.8 1.7-1.9 4.5-.2 6.3l9 9.1 9-9.1c1.7-1.8 1.6-4.6-.2-6.3Z" />
      </svg>
    </button>
  );
}

function ArtistToolsMenu({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const firstButton = menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)');
    firstButton?.focus();

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!containerRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        launcherRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative sm:hidden">
      {isOpen && (
        <div ref={menuRef} className="absolute bottom-full right-0 mb-2 flex flex-col items-end gap-2">
          {children}
        </div>
      )}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        className="z-10 flex h-11 w-11 items-center justify-center rounded border border-white/12 bg-[rgba(10,6,5,0.62)] text-white backdrop-blur-md transition-colors hover:bg-black/70"
        aria-label="Drawing tools"
        aria-expanded={isOpen}
        aria-haspopup="true"
        title="Drawing tools"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
        </svg>
      </button>
    </div>
  );
}

function getNextValueOverlayMode(mode: ValueOverlayMode): ValueOverlayMode {
  if (mode === 'off') return 'grayscale';
  if (mode === 'grayscale') return 'bands-8';
  if (mode === 'bands-8') return 'bands-5';
  if (mode === 'bands-5') return 'bands-3';
  return 'off';
}

function getMediaStreamTrackOrientation(track: MediaStreamTrack): ViewerOrientation | null {
  const settings = track.getSettings();
  const width = settings.width ?? 0;
  const height = settings.height ?? 0;

  if (!width || !height || width === height) return null;
  return height > width ? 'portrait' : 'landscape';
}

function summarizeMediaStreamTrack(track: MediaStreamTrack | null | undefined) {
  if (!track) return null;

  let settings: MediaTrackSettings | null;
  let constraints: MediaTrackConstraints | null;

  try {
    settings = track.getSettings();
  } catch {
    settings = null;
  }

  try {
    constraints = track.getConstraints();
  } catch {
    constraints = null;
  }

  return {
    id: track.id,
    label: track.label || null,
    kind: track.kind,
    enabled: track.enabled,
    muted: track.muted,
    readyState: track.readyState,
    contentHint: track.contentHint || null,
    settings,
    constraints,
  };
}

function summarizePublication(publication: any) {
  if (!publication) return null;

  return {
    trackSid: publication.trackSid ?? publication.sid ?? null,
    trackName: publication.trackName ?? null,
    source: publication.source ?? null,
    kind: publication.kind ?? null,
    isMuted: publication.isMuted ?? null,
    isSubscribed: publication.isSubscribed ?? null,
    track: summarizeMediaStreamTrack(publication.track?.mediaStreamTrack),
  };
}

function summarizeVideoElement(videoEl: HTMLVideoElement | null, expectedTrack: MediaStreamTrack | null | undefined) {
  if (!videoEl) return null;

  const stream = videoEl.srcObject instanceof MediaStream ? videoEl.srcObject : null;
  const rect = videoEl.getBoundingClientRect();
  const style = window.getComputedStyle(videoEl);

  return {
    readyState: videoEl.readyState,
    networkState: videoEl.networkState,
    paused: videoEl.paused,
    ended: videoEl.ended,
    muted: videoEl.muted,
    autoplay: videoEl.autoplay,
    playsInline: videoEl.playsInline,
    videoWidth: videoEl.videoWidth,
    videoHeight: videoEl.videoHeight,
    clientWidth: videoEl.clientWidth,
    clientHeight: videoEl.clientHeight,
    hasSrcObject: Boolean(videoEl.srcObject),
    expectedTrack: summarizeMediaStreamTrack(expectedTrack),
    attachedTracks: stream?.getVideoTracks().map(summarizeMediaStreamTrack) ?? [],
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      top: Math.round(rect.top),
      right: Math.round(rect.right),
      bottom: Math.round(rect.bottom),
      left: Math.round(rect.left),
    },
    css: {
      display: style.display,
      visibility: style.visibility,
      opacity: style.opacity,
      objectFit: style.objectFit,
      transform: style.transform,
    },
    error: videoEl.error
      ? {
          code: videoEl.error.code,
          message: videoEl.error.message || null,
        }
      : null,
  };
}

function summarizeVideoTrackStatsReport(report: RTCStatsReport | null | undefined) {
  if (!report) return null;

  const summaries: Record<string, unknown>[] = [];

  report.forEach((stat: any) => {
    if (!['inbound-rtp', 'outbound-rtp', 'remote-inbound-rtp', 'candidate-pair'].includes(stat.type)) return;
    if (stat.type === 'candidate-pair' && !stat.selected && stat.nominated !== true) return;
    if ((stat.kind ?? stat.mediaType) && (stat.kind ?? stat.mediaType) !== 'video') return;

    summaries.push({
      type: stat.type,
      kind: stat.kind ?? stat.mediaType ?? null,
      bytesSent: stat.bytesSent ?? null,
      bytesReceived: stat.bytesReceived ?? null,
      packetsSent: stat.packetsSent ?? null,
      packetsReceived: stat.packetsReceived ?? null,
      packetsLost: stat.packetsLost ?? null,
      jitter: stat.jitter ?? null,
      roundTripTime: stat.roundTripTime ?? stat.currentRoundTripTime ?? null,
      framesSent: stat.framesSent ?? null,
      framesReceived: stat.framesReceived ?? null,
      framesEncoded: stat.framesEncoded ?? null,
      framesDecoded: stat.framesDecoded ?? null,
      framesDropped: stat.framesDropped ?? null,
      framesPerSecond: stat.framesPerSecond ?? null,
      frameWidth: stat.frameWidth ?? null,
      frameHeight: stat.frameHeight ?? null,
      qualityLimitationReason: stat.qualityLimitationReason ?? null,
      freezeCount: stat.freezeCount ?? null,
      totalFreezesDuration: stat.totalFreezesDuration ?? null,
      pauseCount: stat.pauseCount ?? null,
      totalPausesDuration: stat.totalPausesDuration ?? null,
      availableOutgoingBitrate: stat.availableOutgoingBitrate ?? null,
      availableIncomingBitrate: stat.availableIncomingBitrate ?? null,
    });
  });

  return summaries;
}

function sampleRenderedVideoFrame(videoEl: HTMLVideoElement, canvas: HTMLCanvasElement) {
  if (videoEl.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || videoEl.videoWidth === 0 || videoEl.videoHeight === 0) {
    return {
      sampled: false,
      reason: 'video-not-ready',
      readyState: videoEl.readyState,
      videoWidth: videoEl.videoWidth,
      videoHeight: videoEl.videoHeight,
    };
  }

  const sampleWidth = Math.min(48, videoEl.videoWidth);
  const sampleHeight = Math.min(27, videoEl.videoHeight);
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    return {
      sampled: false,
      reason: 'canvas-context-unavailable',
      readyState: videoEl.readyState,
      videoWidth: videoEl.videoWidth,
      videoHeight: videoEl.videoHeight,
    };
  }

  context.drawImage(videoEl, 0, 0, sampleWidth, sampleHeight);
  const pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
  let lumaTotal = 0;
  let darkPixels = 0;
  let nearBlackPixels = 0;
  const pixelCount = sampleWidth * sampleHeight;

  for (let index = 0; index < pixels.length; index += 4) {
    const r = pixels[index];
    const g = pixels[index + 1];
    const b = pixels[index + 2];
    const luma = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
    lumaTotal += luma;
    if (luma < 16) darkPixels += 1;
    if (luma < 6) nearBlackPixels += 1;
  }

  const averageLuma = lumaTotal / pixelCount;
  const darkPixelRatio = darkPixels / pixelCount;
  const nearBlackPixelRatio = nearBlackPixels / pixelCount;

  return {
    sampled: true,
    sampleWidth,
    sampleHeight,
    videoWidth: videoEl.videoWidth,
    videoHeight: videoEl.videoHeight,
    averageLuma: Number(averageLuma.toFixed(2)),
    darkPixelRatio: Number(darkPixelRatio.toFixed(4)),
    nearBlackPixelRatio: Number(nearBlackPixelRatio.toFixed(4)),
    isBlackFrame: averageLuma < 8 && darkPixelRatio > 0.98,
  };
}

interface FeaturedVideoProps {
  fullScreen?: boolean;
  bwFilter?: boolean;
  canUseArtistTools?: boolean;
  controlsVisible?: boolean;
  showPinSelfControl?: boolean;
  topLeftStatus?: ReactNode;
  poseTransportControls?: ReactNode;
  /** Private viewer pin. Local pins take priority over organizer spotlights. */
  localPinnedParticipantId?: string | null;
  /** Organizer-selected participant spotlight from unified session state. */
  stagePinnedParticipantId?: string | null;
  /** Model camera participant/feed to use while a pose is active. */
  selectedModelCameraParticipantId?: string | null;
  /** Whether this is a pinned/manual selection (shows unpin button) */
  isPinned?: boolean;
  /** Called when user clicks unpin */
  onUnpin?: () => void;
  unpinLabel?: string;
  onLocalCameraPinChange?: (participantId: string | null) => void;
  /** Whether to use active speaker fallback when no model */
  useActiveSpeakerFallback?: boolean;
}

export function FeaturedVideo({
  fullScreen = false,
  bwFilter = false,
  canUseArtistTools,
  controlsVisible = true,
  showPinSelfControl = false,
  topLeftStatus,
  poseTransportControls,
  localPinnedParticipantId,
  stagePinnedParticipantId,
  selectedModelCameraParticipantId,
  isPinned = false,
  onUnpin,
  unpinLabel = 'Unpin',
  onLocalCameraPinChange,
  useActiveSpeakerFallback = true,
}: FeaturedVideoProps) {
  const {
    isHost,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    participants: sessionParticipants,
    poseTimer,
    roomCode,
    socket,
    activeSpeakerTuning,
    viewerOrientationStats,
  } = useRoom();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const localParticipantId = localParticipant?.identity ?? null;
  const room = useRoomContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const selfViewRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastVideoRenderStateSignatureRef = useRef<string | null>(null);
  const modelFeedOrientationRef = useRef<ViewerOrientation | null>(null);
  const viewerOrientationStatsRef = useRef(viewerOrientationStats);
  const orientationBannerTimeoutRef = useRef<number | null>(null);
  const poseHeartCleanupTimersRef = useRef<number[]>([]);
  const activeSpeakerSwitchStateRef = useRef(createActiveSpeakerSwitchState());
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null);
  const [gridDensity, setGridDensity] = useState<GridDensity>('off');
  const [valueOverlayMode, setValueOverlayMode] = useState<ValueOverlayMode>(bwFilter ? 'grayscale' : 'off');
  const [orientationBanner, setOrientationBanner] = useState<{ percent: number } | null>(null);
  const [poseHeartBursts, setPoseHeartBursts] = useState<PoseHeartBurst[]>([]);

  // Grid toggle handler - cycles through: off -> sparse -> dense -> off
  const toggleGrid = () => {
    setGridDensity(prev => {
      if (prev === 'off') return 'sparse';
      if (prev === 'sparse') return 'dense';
      return 'off';
    });
  };
  const primaryModelParticipantId = modelParticipantId ?? modelParticipantIds[0];

  // Check if current user is one of the model camera angles.
  const localParticipantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionParticipants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [localParticipant?.identity, roomCode, sessionParticipants]);
  const isCurrentUserModel = isActiveModelCameraParticipant({
    participantId: localParticipant?.identity,
    participantProfileId: localParticipantProfileId,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
  });
  const sessionModelProfileIds = useMemo(() => new Set(
    sessionParticipants
      .filter(participant => participant.role === 'model' && participant.profileId)
      .map(participant => participant.profileId as string)
  ), [sessionParticipants]);
  const localParticipantIsSessionModel = useMemo(() => {
    if (!localParticipantId) return false;

    const localSessionParticipant = sessionParticipants.find(
      participant => participant.participantId === localParticipantId
    );
    if (localSessionParticipant?.role === 'model') return true;
    if (localParticipantProfileId && sessionModelProfileIds.has(localParticipantProfileId)) return true;

    return modelParticipantIds.includes(localParticipantId);
  }, [localParticipantId, localParticipantProfileId, modelParticipantIds, sessionModelProfileIds, sessionParticipants]);
  const isLocalCameraPinned = !!localParticipantId && localPinnedParticipantId === localParticipantId;
  const canRequestLocalCameraPin =
    !!localParticipantId &&
    !!onLocalCameraPinChange;
  const modelLocalCameraPinParticipantId =
    localParticipantIsSessionModel && isLocalCameraPinned ? localParticipantId : null;

  // Derived from the shared room pose timer; no local pose/break state is kept here.
  const isInActivePose =
    !!poseTimer?.isActive &&
    !poseTimer.isPaused &&
    !poseTimer.isBreak &&
    !poseTimer.isUntimedBreak;
  const showArtistTools = (canUseArtistTools ?? !localParticipantIsSessionModel) && isInActivePose;
  const canSendPoseHeart = showArtistTools && !isCurrentUserModel;
  const isGrayscale = valueOverlayMode === 'grayscale';
  const topLeftZoneClassName = 'absolute left-3 top-3 z-20 flex max-w-[calc(100%-5.5rem)] items-start gap-2 sm:left-4 sm:top-4';
  const bottomLeftZoneClassName = 'absolute bottom-3 left-3 z-20 flex max-w-[calc(50%-1rem)] items-end gap-2 sm:bottom-4 sm:left-4';
  const bottomCenterZoneClassName = 'absolute bottom-3 left-1/2 z-20 -translate-x-1/2 sm:bottom-4';
  const bottomRightZoneClassName = 'absolute bottom-3 right-3 z-20 flex max-w-[calc(50%-1rem)] items-end justify-end gap-2 sm:bottom-4 sm:right-4';
  const controlVisibilityClassName = controlsVisible
    ? 'opacity-100'
    : 'pointer-events-none opacity-0';
  const controlZoneClassName = `${controlVisibilityClassName} transition-opacity duration-300 ease-out`;
  const sendPoseHeart = useCallback(() => {
    if (!socket || !canSendPoseHeart) return;
    socket.emit('pose:heart');
  }, [canSendPoseHeart, socket]);

  useEffect(() => {
    viewerOrientationStatsRef.current = viewerOrientationStats;
  }, [viewerOrientationStats]);

  useEffect(() => {
    if (!socket) return;

    const handlePoseHeart = (payload: PoseHeartPayload) => {
      const bursts: PoseHeartBurst[] = Array.from({ length: POSE_HEARTS_PER_BURST }, (_, index) => ({
        id: `${payload.id}-${index}-${Math.random().toString(36).slice(2)}`,
        groupId: payload.id,
        index,
        seed: Math.random(),
      }));

      setPoseHeartBursts(current => [
        ...current.slice(-30),
        ...bursts,
      ]);

      const cleanupTimer = window.setTimeout(() => {
        setPoseHeartBursts(current => current.filter(burst => burst.groupId !== payload.id));
        poseHeartCleanupTimersRef.current = poseHeartCleanupTimersRef.current.filter(timer => timer !== cleanupTimer);
      }, POSE_HEART_CLEANUP_MS);
      poseHeartCleanupTimersRef.current.push(cleanupTimer);
    };

    socket.on('pose:heart', handlePoseHeart);
    return () => {
      socket.off('pose:heart', handlePoseHeart);
    };
  }, [socket]);

  useEffect(() => {
    if (!isInActivePose) {
      setPoseHeartBursts([]);
    }
  }, [isInActivePose]);

  useEffect(() => () => {
    poseHeartCleanupTimersRef.current.forEach(window.clearTimeout);
    poseHeartCleanupTimersRef.current = [];
  }, []);

  // Track the most recent meaningful remote speaker using LiveKit's ActiveSpeakersChanged event.
  useEffect(() => {
    if (!useActiveSpeakerFallback || !room) {
      activeSpeakerSwitchStateRef.current = createActiveSpeakerSwitchState();
      setActiveSpeakerId(null);
      return;
    }

    const handleActiveSpeakersChanged = (speakers: Participant[]) => {
      const nextState = selectActiveSpeakerUpdate({
        speakers,
        localParticipantId,
        previousState: activeSpeakerSwitchStateRef.current,
        now: Date.now(),
        tuning: activeSpeakerTuning,
      });
      activeSpeakerSwitchStateRef.current = nextState;
      setActiveSpeakerId(nextState.speakerId);
    };

    room.on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);

    // Check initial state
    handleActiveSpeakersChanged(room.activeSpeakers);

    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakersChanged);
    };
  }, [activeSpeakerTuning, localParticipantId, room, useActiveSpeakerFallback]);

  const primaryRemoteParticipantId =
    participants.find(participant => participant.identity !== localParticipantId)?.identity ?? null;
  const activeSpeakerRemoteParticipantId =
    activeSpeakerId &&
    activeSpeakerId !== localParticipantId &&
    participants.some(participant => participant.identity === activeSpeakerId)
      ? activeSpeakerId
      : null;
  const featuredSelection = selectFeaturedParticipant({
    localParticipantId,
    isInActivePose,
    modelLocalCameraPinParticipantId,
    selectedModelCameraParticipantId,
    primaryModelParticipantId,
    localPinnedParticipantId,
    stagePinnedParticipantId,
    activeSpeakerRemoteParticipantId,
    primaryRemoteParticipantId,
    useActiveSpeakerFallback,
  });
  const displayParticipantId = featuredSelection.participantId;
  const activeSpeakerDisplayParticipantId = featuredSelection.isDynamicSpeakerDisplay
    ? displayParticipantId
    : null;
  const isActiveSpeakerDisplay = featuredSelection.isDynamicSpeakerDisplay;

  // Check if the current user is the displayed participant
  const isLocalUserDisplayed = localParticipant?.identity === displayParticipantId;
  const isActiveModelDisplayedByPose =
    isCurrentUserModel &&
    isLocalUserDisplayed &&
    featuredSelection.reason === 'active-pose-model';
  const canPinLocalCamera =
    canRequestLocalCameraPin &&
    (isLocalCameraPinned || !isActiveModelDisplayedByPose);
  const shouldShowTopUnpinControl = isPinned && onUnpin && !isLocalCameraPinned;

  // Find the participant to display - use localParticipant if we're it, otherwise find in participants
  const displayedParticipant = isLocalUserDisplayed
    ? localParticipant
    : participants.find(p => p.identity === displayParticipantId);
  const isChatMode = !isInActivePose;
  const shouldShowDisplayName = isChatMode && !!displayedParticipant && !fullScreen;
  const emptyStateClassName = 'pointer-events-none absolute left-0 right-0 top-1/2 z-10 -translate-y-1/2 px-6 text-center text-[#a59a88]';
  const renderDisplayName = (className: string) => {
    if (!shouldShowDisplayName || !displayedParticipant) return null;

    return (
      <div className={className}>
        {isLocalUserDisplayed ? 'You' : (displayedParticipant.name || displayedParticipant.identity)}
      </div>
    );
  };

  // Get all camera tracks (both local and remote)
  const tracks = useTracks([Track.Source.Camera]);

  // Find the track for our displayed participant
  const trackRef = tracks.find(t => t.participant.identity === displayParticipantId);
  const localCameraTrack = localParticipant?.getTrackPublication(Track.Source.Camera)?.track;
  const localCameraMediaStreamTrackId = localCameraTrack?.mediaStreamTrack?.id ?? null;
  const displayTrack = isLocalUserDisplayed
    ? localCameraTrack ?? trackRef?.publication?.track
    : trackRef?.publication?.track;
  const displayMediaStreamTrackId = displayTrack?.mediaStreamTrack?.id ?? null;
  const videoElementKey = `${displayParticipantId ?? 'none'}:${displayMediaStreamTrackId ?? 'no-track'}:${isLocalUserDisplayed ? 'local' : 'remote'}`;
  const shouldShowSelfView = false;
  const selfViewTrack = localParticipant?.getTrackPublication(Track.Source.Camera)?.track;
  const selfViewMediaStreamTrackId = selfViewTrack?.mediaStreamTrack?.id ?? null;

  useEffect(() => {
    if (!isCurrentUserModel || !localCameraTrack?.mediaStreamTrack) {
      modelFeedOrientationRef.current = null;
      if (orientationBannerTimeoutRef.current !== null) {
        window.clearTimeout(orientationBannerTimeoutRef.current);
        orientationBannerTimeoutRef.current = null;
      }
      setOrientationBanner(null);
      return;
    }

    const mediaStreamTrack = localCameraTrack.mediaStreamTrack;
    setOrientationBanner(null);
    modelFeedOrientationRef.current = getMediaStreamTrackOrientation(mediaStreamTrack);

    const showBannerForOrientation = (orientation: ViewerOrientation) => {
      const stats = viewerOrientationStatsRef.current;
      const percent = orientation === 'portrait'
        ? stats.portraitPercent
        : stats.landscapePercent;

      if (orientationBannerTimeoutRef.current !== null) {
        window.clearTimeout(orientationBannerTimeoutRef.current);
      }

      setOrientationBanner({ percent });
      orientationBannerTimeoutRef.current = window.setTimeout(() => {
        setOrientationBanner(null);
        orientationBannerTimeoutRef.current = null;
      }, 2200);
    };

    const checkOrientation = () => {
      const nextOrientation = getMediaStreamTrackOrientation(mediaStreamTrack);
      if (!nextOrientation) return;

      const previousOrientation = modelFeedOrientationRef.current;
      modelFeedOrientationRef.current = nextOrientation;

      if (previousOrientation && previousOrientation !== nextOrientation) {
        showBannerForOrientation(nextOrientation);
      }
    };

    const interval = window.setInterval(checkOrientation, 700);

    return () => {
      window.clearInterval(interval);
      if (orientationBannerTimeoutRef.current !== null) {
        window.clearTimeout(orientationBannerTimeoutRef.current);
        orientationBannerTimeoutRef.current = null;
      }
    };
  }, [isCurrentUserModel, localCameraMediaStreamTrackId, localCameraTrack]);

  useEffect(() => {
    const localCameraPublication = localParticipant?.getTrackPublication(Track.Source.Camera);
    const displayPublication = isLocalUserDisplayed
      ? localCameraPublication
      : trackRef?.publication;
    const summary = {
      displayParticipantId,
      displayedParticipant: displayedParticipant
        ? {
            identity: displayedParticipant.identity,
            name: displayedParticipant.name || null,
            isLocal: displayedParticipant.isLocal,
            isCameraEnabled: displayedParticipant.isCameraEnabled,
            isMicrophoneEnabled: displayedParticipant.isMicrophoneEnabled,
          }
        : null,
      selection: {
        selectionReason: featuredSelection.reason,
        localPinnedParticipantId,
        selectedModelCameraParticipantId,
        stagePinnedParticipantId,
        activeSpeakerId,
        activeSpeakerDisplayParticipantId,
        primaryRemoteParticipantId,
        activeSpeakerRemoteParticipantId,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        primaryModelParticipantId,
        localParticipantProfileId,
        useActiveSpeakerFallback,
        isActiveSpeakerDisplay,
        isInActivePose,
        isCurrentUserModel,
        isLocalUserDisplayed,
        shouldShowSelfView,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        visualViewportWidth: window.visualViewport?.width ?? null,
        visualViewportHeight: window.visualViewport?.height ?? null,
      },
      displayPublication: summarizePublication(displayPublication),
      localCameraPublication: summarizePublication(localCameraPublication),
      videoElement: summarizeVideoElement(videoRef.current, displayTrack?.mediaStreamTrack),
      selfView: {
        mediaStreamTrackId: selfViewMediaStreamTrackId,
        publication: summarizePublication(localCameraPublication),
        videoElement: summarizeVideoElement(selfViewRef.current, selfViewTrack?.mediaStreamTrack),
      },
    };
    const signature = JSON.stringify({
      displayParticipantId,
      selectionReason: featuredSelection.reason,
      displayedParticipantIdentity: displayedParticipant?.identity ?? null,
      displayedParticipantCameraEnabled: displayedParticipant?.isCameraEnabled ?? null,
      displayMediaStreamTrackId,
      localCameraTrackId: localCameraPublication?.track?.mediaStreamTrack?.id ?? null,
      selfViewMediaStreamTrackId,
      isCurrentUserModel,
      isInActivePose,
      isLocalUserDisplayed,
      shouldShowSelfView,
      localPinnedParticipantId,
      selectedModelCameraParticipantId,
      stagePinnedParticipantId,
      activeSpeakerId,
      activeSpeakerDisplayParticipantId,
      activeSpeakerRemoteParticipantId,
      primaryRemoteParticipantId,
      modelParticipantId,
      modelProfileId,
      modelParticipantIds,
      primaryModelParticipantId,
      localParticipantProfileId,
      isActiveSpeakerDisplay,
    });

    if (lastVideoRenderStateSignatureRef.current === signature) return;
    lastVideoRenderStateSignatureRef.current = signature;

    logVideoDiagnostic('info', 'featured-video-render-state', summary, {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
    });
  }, [
    activeSpeakerId,
    activeSpeakerDisplayParticipantId,
    activeSpeakerRemoteParticipantId,
    displayMediaStreamTrackId,
    displayParticipantId,
    displayTrack,
    displayedParticipant,
    featuredSelection.reason,
    isActiveSpeakerDisplay,
    isCurrentUserModel,
    isInActivePose,
    isLocalUserDisplayed,
    localParticipant,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    localParticipantProfileId,
    localPinnedParticipantId,
    primaryModelParticipantId,
    primaryRemoteParticipantId,
    selectedModelCameraParticipantId,
    roomCode,
    selfViewMediaStreamTrackId,
    selfViewTrack,
    shouldShowSelfView,
    stagePinnedParticipantId,
    trackRef?.publication,
    useActiveSpeakerFallback,
  ]);

  useEffect(() => {
    const videoEl = videoRef.current;
    const track = displayTrack as any;
    const mediaStreamTrack = track?.mediaStreamTrack as MediaStreamTrack | undefined;

    if (!videoEl || !track || !mediaStreamTrack) return;

    let cancelled = false;
    let consecutiveBlackSamples = 0;
    let blackStartedAt: number | null = null;
    let blackWarningLogged = false;
    let lastStatsLoggedAt = 0;
    const canvas = document.createElement('canvas');
    const localCameraPublication = localParticipant?.getTrackPublication(Track.Source.Camera);
    const displayPublication = isLocalUserDisplayed
      ? localCameraPublication
      : trackRef?.publication;

    const getAttachedTrackIds = () => {
      const stream = videoEl.srcObject instanceof MediaStream ? videoEl.srcObject : null;
      return stream?.getVideoTracks().map(track => track.id) ?? [];
    };

    const getDiagnosticDetails = (details: Record<string, unknown> = {}) => ({
      ...details,
      displayParticipantId,
      displayedParticipant: displayedParticipant
        ? {
            identity: displayedParticipant.identity,
            name: displayedParticipant.name || null,
            isLocal: displayedParticipant.isLocal,
            isCameraEnabled: displayedParticipant.isCameraEnabled,
            isMicrophoneEnabled: displayedParticipant.isMicrophoneEnabled,
            connectionQuality: displayedParticipant.connectionQuality,
          }
        : null,
      selection: {
        reason: featuredSelection.reason,
        localPinnedParticipantId,
        selectedModelCameraParticipantId,
        stagePinnedParticipantId,
        activeSpeakerId,
        activeSpeakerDisplayParticipantId,
        activeSpeakerRemoteParticipantId,
        primaryRemoteParticipantId,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        primaryModelParticipantId,
        isActiveSpeakerDisplay,
        isInActivePose,
        isLocalUserDisplayed,
        useActiveSpeakerFallback,
      },
      publication: summarizePublication(displayPublication),
      attachedTrackIds: getAttachedTrackIds(),
      videoElement: summarizeVideoElement(videoEl, mediaStreamTrack),
    });

    const logStageVideoState = (
      level: 'debug' | 'info' | 'warn' | 'error',
      event: string,
      details: Record<string, unknown> = {}
    ) => {
      logVideoDiagnostic(level, event, getDiagnosticDetails(details), {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
      });
    };

    const collectStats = async (reason: string) => {
      try {
        const report = await track.getRTCStatsReport?.();
        if (cancelled) return;
        logStageVideoState('info', 'featured-video-render-stats-snapshot', {
          reason,
          stats: summarizeVideoTrackStatsReport(report),
        });
      } catch (error) {
        if (cancelled) return;
        logStageVideoState('warn', 'featured-video-render-stats-failed', {
          reason,
          error,
        });
      }
    };

    const sampleFrame = () => {
      let sample: ReturnType<typeof sampleRenderedVideoFrame>;
      try {
        sample = sampleRenderedVideoFrame(videoEl, canvas);
      } catch (error) {
        logStageVideoState('warn', 'featured-video-render-frame-sample-failed', { error });
        return;
      }

      if (!sample.sampled) return;

      if (sample.isBlackFrame) {
        consecutiveBlackSamples += 1;
        blackStartedAt ??= Date.now();

        if (consecutiveBlackSamples >= 3 && !blackWarningLogged) {
          blackWarningLogged = true;
          logStageVideoState('warn', 'featured-video-rendered-black-sustained', {
            sample,
            consecutiveBlackSamples,
            blackDurationMs: blackStartedAt ? Date.now() - blackStartedAt : null,
          });
          void collectStats('rendered-black-sustained');
        }
        return;
      }

      if (blackWarningLogged) {
        logStageVideoState('info', 'featured-video-rendered-black-recovered', {
          sample,
          previousConsecutiveBlackSamples: consecutiveBlackSamples,
          blackDurationMs: blackStartedAt ? Date.now() - blackStartedAt : null,
        });
      }

      consecutiveBlackSamples = 0;
      blackStartedAt = null;
      blackWarningLogged = false;
    };

    const handleVideoElementEvent = (event: Event) => {
      const eventName = event.type;
      const level =
        eventName === 'error' || eventName === 'emptied' || eventName === 'stalled'
          ? 'warn'
          : eventName === 'playing' || eventName === 'loadedmetadata' || eventName === 'resize'
            ? 'info'
            : 'debug';

      logStageVideoState(level, `featured-video-element-${eventName}`);
    };
    const handleMediaStreamTrackEvent = (event: Event) => {
      logStageVideoState(event.type === 'ended' ? 'warn' : 'info', `featured-video-track-${event.type}`, {
        mediaStreamTrack: summarizeMediaStreamTrack(mediaStreamTrack),
      });
    };
    const handleVisibilityChange = () => {
      logStageVideoState(document.visibilityState === 'visible' ? 'info' : 'debug', 'featured-video-page-visibility-changed', {
        visibilityState: document.visibilityState,
      });
      if (document.visibilityState === 'visible') {
        void collectStats('visibility-visible');
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      logStageVideoState('info', 'featured-video-pageshow', {
        persisted: event.persisted,
      });
      void collectStats('pageshow');
    };

    logStageVideoState('info', 'featured-video-render-monitor-started', {
      mediaStreamTrack: summarizeMediaStreamTrack(mediaStreamTrack),
    });

    const videoElementEvents = ['loadedmetadata', 'playing', 'pause', 'waiting', 'stalled', 'suspend', 'emptied', 'resize', 'error'];
    videoElementEvents.forEach((eventName) => {
      videoEl.addEventListener(eventName, handleVideoElementEvent);
    });
    mediaStreamTrack.addEventListener('mute', handleMediaStreamTrackEvent);
    mediaStreamTrack.addEventListener('unmute', handleMediaStreamTrackEvent);
    mediaStreamTrack.addEventListener('ended', handleMediaStreamTrackEvent);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    const interval = window.setInterval(() => {
      sampleFrame();
      const now = Date.now();
      if (now - lastStatsLoggedAt >= 20_000) {
        lastStatsLoggedAt = now;
        void collectStats('interval');
      }
    }, 3_000);

    const startupTimer = window.setTimeout(() => {
      if (cancelled) return;
      sampleFrame();
      void collectStats('startup');
    }, 1500);

    return () => {
      cancelled = true;
      videoElementEvents.forEach((eventName) => {
        videoEl.removeEventListener(eventName, handleVideoElementEvent);
      });
      mediaStreamTrack.removeEventListener('mute', handleMediaStreamTrackEvent);
      mediaStreamTrack.removeEventListener('unmute', handleMediaStreamTrackEvent);
      mediaStreamTrack.removeEventListener('ended', handleMediaStreamTrackEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.clearInterval(interval);
      window.clearTimeout(startupTimer);
      logStageVideoState('info', 'featured-video-render-monitor-stopped', {
        consecutiveBlackSamples,
        blackWarningLogged,
      });
    };
  }, [
    activeSpeakerDisplayParticipantId,
    activeSpeakerId,
    activeSpeakerRemoteParticipantId,
    displayMediaStreamTrackId,
    displayParticipantId,
    displayTrack,
    displayedParticipant,
    featuredSelection.reason,
    isActiveSpeakerDisplay,
    isInActivePose,
    isLocalUserDisplayed,
    localParticipant,
    localPinnedParticipantId,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    primaryModelParticipantId,
    primaryRemoteParticipantId,
    roomCode,
    selectedModelCameraParticipantId,
    stagePinnedParticipantId,
    trackRef?.publication,
    useActiveSpeakerFallback,
  ]);

  useEffect(() => {
    const videoEl = selfViewRef.current;
    const track = selfViewTrack;
    const mediaStreamTrack = track?.mediaStreamTrack;

    if (!videoEl || !track || !mediaStreamTrack || !shouldShowSelfView) return;

    let cancelled = false;

    const getAttachedTrackId = () => {
      const stream = videoEl.srcObject instanceof MediaStream ? videoEl.srcObject : null;
      return stream?.getVideoTracks()[0]?.id ?? null;
    };

    const logSelfViewState = (
      level: 'debug' | 'info' | 'warn' | 'error',
      event: string,
      details: Record<string, unknown> = {}
    ) => {
      logVideoDiagnostic(level, event, {
        ...details,
        displayParticipantId,
        videoElement: summarizeVideoElement(videoEl, track.mediaStreamTrack),
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
      });
    };

    const attachAndPlay = (reason: string) => {
      if (cancelled) return;

      const expectedTrackId = mediaStreamTrack.id;
      const attachedTrackId = getAttachedTrackId();

      try {
        if (attachedTrackId !== expectedTrackId) {
          videoEl.srcObject = new MediaStream([mediaStreamTrack]);
        }

        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.autoplay = true;
        videoEl.play().catch((error) => {
          if (cancelled) return;
          logSelfViewState('warn', 'featured-self-view-playback-failed', {
            reason,
            error,
            expectedTrackId,
            attachedTrackId: getAttachedTrackId(),
          });
        });
      } catch (error) {
        logSelfViewState('error', 'featured-self-view-attach-failed', {
          reason,
          error,
          expectedTrackId,
          attachedTrackId,
        });
      }
    };

    const recoverSelfView = (reason: string) => {
      const expectedTrackId = mediaStreamTrack.id;
      const attachedTrackId = getAttachedTrackId();
      const shouldRecover =
        attachedTrackId !== expectedTrackId ||
        videoEl.paused ||
        videoEl.readyState < 2 ||
        videoEl.videoWidth === 0 ||
        videoEl.videoHeight === 0;

      if (!shouldRecover) return;

      logSelfViewState('warn', 'featured-self-view-recovering', {
        reason,
        expectedTrackId,
        attachedTrackId,
        readyState: videoEl.readyState,
        paused: videoEl.paused,
        videoWidth: videoEl.videoWidth,
        videoHeight: videoEl.videoHeight,
        mediaTrackReadyState: mediaStreamTrack.readyState,
      });

      videoEl.srcObject = null;
      attachAndPlay(reason);
    };

    const handleCanPlay = () => attachAndPlay('canplay');
    const handleCameraRefresh = () => recoverSelfView('model-camera-refreshed');
    const handlePageShow = () => recoverSelfView('pageshow');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recoverSelfView('visibility-visible');
      }
    };

    attachAndPlay('mount');

    videoEl.addEventListener('canplay', handleCanPlay);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('drawclub:camera-device-track-refreshed', handleCameraRefresh);

    const interval = window.setInterval(() => recoverSelfView('self-view-watchdog'), 2500);

    return () => {
      cancelled = true;
      videoEl.removeEventListener('canplay', handleCanPlay);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('drawclub:camera-device-track-refreshed', handleCameraRefresh);
      window.clearInterval(interval);
      videoEl.srcObject = null;
    };
  }, [
    displayParticipantId,
    localParticipant?.identity,
    localParticipant?.name,
    roomCode,
    selfViewMediaStreamTrackId,
    selfViewTrack,
    shouldShowSelfView,
  ]);

  // Attach video track to element. Local self-view on mobile Safari can go black
  // after camera restarts or page visibility changes while the track is still
  // publishing correctly, so local preview gets a lightweight reattach/playback
  // recovery loop.
  useEffect(() => {
    const videoEl = videoRef.current;
    const track = displayTrack;

    if (!videoEl || !track) return;

    let cancelled = false;

    const getAttachedTrackId = () => {
      const stream = videoEl.srcObject instanceof MediaStream ? videoEl.srcObject : null;
      return stream?.getVideoTracks()[0]?.id ?? null;
    };

    const getVideoElementState = () => summarizeVideoElement(videoEl, track.mediaStreamTrack);

    const logLocalPreviewState = (
      level: 'debug' | 'info' | 'warn' | 'error',
      event: string,
      details: Record<string, unknown> = {}
    ) => {
      if (!isLocalUserDisplayed) return;
      logVideoDiagnostic(level, event, {
        ...details,
        displayParticipantId,
        videoElement: getVideoElementState(),
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
      });
    };

    const attachAndPlay = (reason: string) => {
      if (cancelled) return;
      const expectedTrackId = track.mediaStreamTrack?.id ?? null;
      const attachedTrackId = getAttachedTrackId();

      try {
        if (reason !== 'canplay') {
          logLocalPreviewState('info', 'featured-local-preview-attach-requested', {
            reason,
            expectedTrackId,
            attachedTrackId,
            willAttach: attachedTrackId !== expectedTrackId,
          });
        }
        if (attachedTrackId !== expectedTrackId) {
          track.attach(videoEl);
        }
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.autoplay = true;
        const playPromise = videoEl.play();
        if (playPromise) {
          playPromise.then(() => {
            if (cancelled || reason === 'canplay') return;
            logLocalPreviewState('info', 'featured-local-preview-playback-started', {
              reason,
              expectedTrackId,
              attachedTrackId: getAttachedTrackId(),
            });
          }).catch((error) => {
            if (cancelled) return;
            logVideoDiagnostic('warn', 'featured-video-playback-retry-failed', {
              reason,
              error,
              displayParticipantId,
              isLocalUserDisplayed,
              expectedTrackId,
              attachedTrackId: getAttachedTrackId(),
              readyState: videoEl.readyState,
              paused: videoEl.paused,
              videoWidth: videoEl.videoWidth,
              videoHeight: videoEl.videoHeight,
              videoElement: getVideoElementState(),
            }, {
              roomCode,
              participantId: localParticipant?.identity ?? null,
              participantName: localParticipant?.name ?? null,
            });
          });
        }
      } catch (error) {
        logVideoDiagnostic('error', 'featured-video-attach-failed', {
          reason,
          error,
          displayParticipantId,
          isLocalUserDisplayed,
          expectedTrackId,
          attachedTrackId,
          videoElement: getVideoElementState(),
        }, {
          roomCode,
          participantId: localParticipant?.identity ?? null,
          participantName: localParticipant?.name ?? null,
        });
      }
    };

    attachAndPlay('mount');

    const recoverPlayback = (reason: string) => {
      const expectedTrackId = track.mediaStreamTrack?.id ?? null;
      const attachedTrackId = getAttachedTrackId();
      const shouldRecover =
        attachedTrackId !== expectedTrackId ||
        videoEl.paused ||
        videoEl.readyState < 2 ||
        videoEl.videoWidth === 0 ||
        videoEl.videoHeight === 0;

      if (shouldRecover) {
        logVideoDiagnostic('warn', 'featured-local-preview-recovering', {
          reason,
          displayParticipantId,
          expectedTrackId,
          attachedTrackId,
          readyState: videoEl.readyState,
          paused: videoEl.paused,
          videoWidth: videoEl.videoWidth,
          videoHeight: videoEl.videoHeight,
          mediaTrackReadyState: track.mediaStreamTrack?.readyState ?? null,
          videoElement: getVideoElementState(),
        }, {
          roomCode,
          participantId: localParticipant?.identity ?? null,
          participantName: localParticipant?.name ?? null,
        });
        if (attachedTrackId !== expectedTrackId) {
          track.detach(videoEl);
        }
        attachAndPlay(reason);
      }
    };

    const handleVisibilityChange = () => {
      logLocalPreviewState(
        document.visibilityState === 'visible' ? 'info' : 'debug',
        'featured-local-preview-page-visibility-changed',
        { visibilityState: document.visibilityState }
      );
      if (document.visibilityState === 'visible') {
        recoverPlayback('visibility-visible');
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      logLocalPreviewState('info', 'featured-local-preview-pageshow', {
        persisted: event.persisted,
      });
      recoverPlayback('pageshow');
    };
    const handleCameraRefresh = () => {
      logLocalPreviewState('info', 'featured-local-preview-camera-track-refreshed');
      recoverPlayback('model-camera-refreshed');
    };
    const handleCanPlay = () => {
      logLocalPreviewState('debug', 'featured-local-preview-video-canplay');
      attachAndPlay('canplay');
    };
    const handleVideoElementEvent = (event: Event) => {
      const eventName = event.type;
      const level =
        eventName === 'error' || eventName === 'emptied'
          ? 'warn'
          : eventName === 'playing' || eventName === 'loadedmetadata'
            ? 'info'
            : 'debug';
      logLocalPreviewState(level, `featured-local-preview-video-${eventName}`);
    };
    const mediaStreamTrack = track.mediaStreamTrack ?? null;
    const handleMediaStreamTrackEvent = (event: Event) => {
      logLocalPreviewState(event.type === 'ended' ? 'warn' : 'info', `featured-local-preview-track-${event.type}`, {
        mediaStreamTrack: summarizeMediaStreamTrack(mediaStreamTrack),
      });
    };
    const videoElementEvents = ['loadedmetadata', 'playing', 'pause', 'waiting', 'stalled', 'suspend', 'emptied', 'resize', 'error'];

    videoEl.addEventListener('canplay', handleCanPlay);
    videoElementEvents.forEach((eventName) => {
      videoEl.addEventListener(eventName, handleVideoElementEvent);
    });
    mediaStreamTrack?.addEventListener('mute', handleMediaStreamTrackEvent);
    mediaStreamTrack?.addEventListener('unmute', handleMediaStreamTrackEvent);
    mediaStreamTrack?.addEventListener('ended', handleMediaStreamTrackEvent);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('drawclub:camera-device-track-refreshed', handleCameraRefresh);

    const interval = isLocalUserDisplayed
      ? window.setInterval(() => recoverPlayback('local-preview-watchdog'), 2500)
      : null;

    return () => {
      cancelled = true;
      videoEl.removeEventListener('canplay', handleCanPlay);
      videoElementEvents.forEach((eventName) => {
        videoEl.removeEventListener(eventName, handleVideoElementEvent);
      });
      mediaStreamTrack?.removeEventListener('mute', handleMediaStreamTrackEvent);
      mediaStreamTrack?.removeEventListener('unmute', handleMediaStreamTrackEvent);
      mediaStreamTrack?.removeEventListener('ended', handleMediaStreamTrackEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('drawclub:camera-device-track-refreshed', handleCameraRefresh);
      if (interval !== null) {
        window.clearInterval(interval);
      }
      track.detach(videoEl);
    };
  }, [
    displayTrack,
    displayMediaStreamTrackId,
    displayParticipantId,
    isLocalUserDisplayed,
    localParticipant?.identity,
    localParticipant?.name,
    roomCode,
  ]);

  const videoClassName = 'absolute inset-0 w-full h-full object-contain';
  const valueOverlayClassName = 'absolute inset-0 z-[1] h-full w-full object-contain pointer-events-none';
  const videoStyle: CSSProperties = {
    filter: isGrayscale ? 'grayscale(100%)' : 'none',
    transform: isLocalUserDisplayed ? 'scaleX(-1)' : undefined,
    transition: 'filter 0.2s ease-in-out',
  };
  const renderTopLeftStatus = () => {
    if (topLeftStatus) {
      return (
        <div className={topLeftZoneClassName}>
          {topLeftStatus}
        </div>
      );
    }

    if (!shouldShowDisplayName) return null;

    return (
      <div className={topLeftZoneClassName}>
        {renderDisplayName('max-w-[160px] truncate bg-[rgba(10,6,5,0.62)] backdrop-blur-md px-2.5 py-1 rounded border border-white/12 text-xs text-[#f1ece3] font-mono')}
      </div>
    );
  };

  const renderArtistTools = () => {
    if (!showArtistTools && !shouldShowTopUnpinControl) return null;

    const renderToolButtons = () => (
      <>
        {showArtistTools && (
          <GridToggleButton density={gridDensity} onToggle={toggleGrid} className="h-11 w-11 px-0 sm:h-8 sm:w-auto sm:px-2.5" />
        )}
        {showArtistTools && (
          <BWToggleButton
            mode={valueOverlayMode}
            onToggle={() => setValueOverlayMode(getNextValueOverlayMode)}
            className="h-11 min-w-11 px-2 sm:h-8 sm:min-w-0 sm:px-2.5"
          />
        )}
        {canSendPoseHeart && (
          <PoseHeartButton onClick={sendPoseHeart} />
        )}
        {shouldShowTopUnpinControl && (
          <button
            onClick={onUnpin}
            className="z-10 flex h-11 items-center gap-1.5 rounded border border-white/12 bg-[rgba(10,6,5,0.62)] px-2.5 text-[11px] font-medium text-white backdrop-blur-md transition-colors hover:bg-black/70 sm:h-8"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            {unpinLabel}
          </button>
        )}
      </>
    );

    return (
      <div className={`${bottomRightZoneClassName} ${controlZoneClassName}`}>
        <div className="hidden items-center gap-2 sm:flex">
          {renderToolButtons()}
        </div>
        <ArtistToolsMenu>
          {renderToolButtons()}
        </ArtistToolsMenu>
      </div>
    );
  };

  const renderPoseTransportControls = () => {
    if (!fullScreen || !poseTransportControls) return null;

    return (
      <div className={`${bottomLeftZoneClassName} ${controlZoneClassName}`}>
        {poseTransportControls}
      </div>
    );
  };

  const renderControlZones = (mediaControlsFallbackClassName = bottomCenterZoneClassName) => (
    <>
      {renderTopLeftStatus()}
      {renderArtistTools()}
      {renderPoseTransportControls()}
      {renderMediaControls(mediaControlsFallbackClassName)}
    </>
  );
  const renderMediaControls = (fallbackClassName: string) => (
    <MediaControls
      className={`${fallbackClassName} ${controlZoneClassName}`}
      canPinLocalCamera={showPinSelfControl && canPinLocalCamera}
      isLocalCameraPinned={isLocalCameraPinned}
      showMutedSpeakingWarning={controlsVisible}
      onToggleLocalCameraPin={
        localParticipantId && onLocalCameraPinChange
          ? () => onLocalCameraPinChange(isLocalCameraPinned ? null : localParticipantId)
          : undefined
      }
    />
  );
  const renderOrientationBanner = () => {
    if (!orientationBanner || !isCurrentUserModel) return null;

    return (
      <div className="absolute top-4 left-1/2 z-30 max-w-[calc(100%-1.5rem)] -translate-x-1/2 rounded border border-white/15 bg-[rgba(10,6,5,0.78)] px-3 py-2 text-center text-[12px] font-medium text-white shadow-lg backdrop-blur-md">
        {orientationBanner.percent}% of participants are in this orientation
      </div>
    );
  };
  const renderPoseHeartBursts = () => {
    if (!isInActivePose || poseHeartBursts.length === 0) return null;

    return (
      <div className={`pose-heart-corner ${isCurrentUserModel ? 'pose-heart-corner-model' : ''}`} aria-hidden="true">
        {poseHeartBursts.map((burst) => {
          const lane = burst.index - (POSE_HEARTS_PER_BURST - 1) / 2;
          const laneOffset = Math.round(lane * 7 + (burst.seed - 0.5) * 12);
          const waveDirection = burst.index % 2 === 0 ? 1 : -1;
          const wavePeak = Math.round((24 + ((burst.seed * 19 + burst.index * 2.7) % 18)) * waveDirection);
          const waveSoft = Math.round(wavePeak * 0.5);
          const waveStrong = Math.round(wavePeak * 0.86);
          const spin = Math.round((burst.seed - 0.5) * 18);
          const scale = 0.72 + ((burst.seed * 10 + burst.index * 0.13) % 0.45);
          const delay = burst.index * POSE_HEART_STAGGER_MS;
          const style = {
            '--pose-heart-lane': `${laneOffset}px`,
            '--pose-heart-wave-soft': `${waveSoft}px`,
            '--pose-heart-wave-strong': `${waveStrong}px`,
            '--pose-heart-wave-peak': `${wavePeak}px`,
            '--pose-heart-wave-soft-reverse': `${-waveSoft}px`,
            '--pose-heart-wave-strong-reverse': `${-waveStrong}px`,
            '--pose-heart-wave-peak-reverse': `${-wavePeak}px`,
            '--pose-heart-rotate-start': `${Math.round(spin * -0.4)}deg`,
            '--pose-heart-rotate-end': `${Math.round(spin * 0.7)}deg`,
            '--pose-heart-scale': scale.toFixed(2),
            '--pose-heart-end-scale': (scale * 0.66).toFixed(2),
            '--pose-heart-delay': `${delay}ms`,
            '--pose-heart-duration': `${POSE_HEART_ANIMATION_MS}ms`,
          } as CSSProperties;

          return (
            <span key={burst.id} className="pose-heart-float" style={style}>
              <span className="pose-heart-rise">
                <span className="pose-heart-sway">
                  <span className="pose-heart-shape">
                    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.8 4.6c-1.7-1.6-4.4-1.5-6 .2L12 7.7 9.2 4.8c-1.6-1.7-4.3-1.8-6-.2-1.8 1.7-1.9 4.5-.2 6.3l9 9.1 9-9.1c1.7-1.8 1.6-4.6-.2-6.3Z" />
                    </svg>
                  </span>
                </span>
              </span>
            </span>
          );
        })}
      </div>
    );
  };
  const renderSelfView = (className: string) => {
    if (!shouldShowSelfView) return null;

    return (
      <div className={className}>
        {selfViewTrack ? (
          <video
            ref={selfViewRef}
            className="h-full w-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
            autoPlay
            playsInline
            muted
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-3 text-center text-xs text-white/65">
            Camera off
          </div>
        )}
      </div>
    );
  };
  const renderTrackUnavailable = (
    mediaControlsFallbackClassName: string,
    selfViewClassName: string
  ) => (
    <>
      <div className="text-[#a59a88]">
        {displayedParticipant?.isCameraEnabled ? 'Loading video...' : 'Camera is off'}
      </div>
      {renderMediaControls(mediaControlsFallbackClassName)}
      {renderSelfView(selfViewClassName)}
    </>
  );
  const fullScreenSelfViewClassName = 'absolute bottom-[8.25rem] right-3 z-20 h-[13%] min-h-[58px] max-h-[88px] aspect-video overflow-hidden rounded border border-white/15 bg-black shadow-lg sm:bottom-4 sm:right-4 sm:h-[22%] sm:min-h-[96px] sm:max-h-[180px]';
  const normalSelfViewClassName = 'absolute bottom-[4.75rem] right-3 z-20 h-[16%] min-h-[56px] max-h-[88px] aspect-video overflow-hidden rounded border border-white/15 bg-black shadow-lg sm:bottom-3 sm:h-[22%] sm:min-h-[88px] sm:max-h-[160px]';

  if (!displayParticipantId) {
    return (
      <div ref={containerRef} className={`${fullScreen ? 'h-full w-full' : 'absolute inset-0'} relative flex items-center justify-center ${fullScreen ? 'bg-black' : 'bg-[#0a0807]'}`}>
        {renderControlZones()}
        <div className={emptyStateClassName}>
          <div>
            <p className="text-lg mb-2">
              {useActiveSpeakerFallback ? 'Waiting for someone to speak' : 'No model selected'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!displayedParticipant) {
    return (
      <div ref={containerRef} className={`${fullScreen ? 'h-full w-full' : 'absolute inset-0'} relative flex items-center justify-center ${fullScreen ? 'bg-black' : 'bg-[#0a0807]'}`}>
        {renderControlZones()}
        <div className={emptyStateClassName}>
          <div>
            <p className="text-lg mb-2">Model disconnected</p>
            {!fullScreen && (
              <p className="text-sm text-[#6e655a]">Select another participant to be the model</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fullscreen mode - just the video
  if (fullScreen) {
    const hasTrack = !!displayTrack;
    return (
      <div ref={containerRef} className="h-full w-full relative flex items-center justify-center bg-black">
        {hasTrack ? (
          <>
            <video
              key={videoElementKey}
              ref={videoRef}
              className={videoClassName}
              style={videoStyle}
              autoPlay
              playsInline
              muted
            />
            {showArtistTools && (
              <ValueAnalysisOverlay
                mode={valueOverlayMode}
                videoRef={videoRef}
                imageRef={imageRef}
                sourceType="video"
                sourceKey={displayParticipantId}
                className={valueOverlayClassName}
                style={videoStyle}
              />
            )}
            {renderTopLeftStatus()}
            {renderOrientationBanner()}
            {renderArtistTools()}
            {renderPoseTransportControls()}
            {/* Grid overlay for proportion reference */}
            {showArtistTools && <GridOverlay density={gridDensity} containerRef={containerRef} />}
            {renderMediaControls(bottomCenterZoneClassName)}
            {renderPoseHeartBursts()}
            {renderSelfView(fullScreenSelfViewClassName)}
          </>
        ) : (
          <>
            {renderOrientationBanner()}
            {renderControlZones()}
            {renderTrackUnavailable(
              bottomCenterZoneClassName,
              fullScreenSelfViewClassName
            )}
            {renderPoseHeartBursts()}
          </>
        )}
      </div>
    );
  }

  // Normal mode - dark video tile
  const hasTrackNormal = !!displayTrack;
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0a0807]">
      {/* Video display */}
      <div ref={containerRef} className="absolute inset-0 flex items-center justify-center bg-[#0a0807]">
        {hasTrackNormal ? (
          <>
            <video
              key={videoElementKey}
              ref={videoRef}
              className={videoClassName}
              style={videoStyle}
              autoPlay
              playsInline
              muted
            />
            {showArtistTools && (
              <ValueAnalysisOverlay
                mode={valueOverlayMode}
                videoRef={videoRef}
                imageRef={imageRef}
                sourceType="video"
                sourceKey={displayParticipantId}
                className={valueOverlayClassName}
                style={videoStyle}
              />
            )}
            {/* Gradient overlay at bottom */}
            <div className="absolute inset-0 z-[2] pointer-events-none bg-gradient-to-t from-[rgba(20,12,6,0.3)] to-transparent" />
            {renderTopLeftStatus()}
            {renderOrientationBanner()}
            {renderArtistTools()}
            {renderPoseTransportControls()}
            {/* Grid overlay for proportion reference */}
            {showArtistTools && <GridOverlay density={gridDensity} containerRef={containerRef} />}
            {renderMediaControls(bottomCenterZoneClassName)}
            {renderPoseHeartBursts()}
            {renderSelfView(normalSelfViewClassName)}
          </>
        ) : (
          <>
            {renderOrientationBanner()}
            {renderControlZones()}
            {renderTrackUnavailable(
              bottomCenterZoneClassName,
              normalSelfViewClassName
            )}
            {renderPoseHeartBursts()}
          </>
        )}
      </div>
    </div>
  );
}
